import { API_ERROR_MESSAGES_HE, type ApiErrorBody, type ApiErrorCode, type HealthResponse, type MeResponse } from '@crew/shared';
import type { User } from 'firebase/auth';
import { webConfig } from '@/config/env';
import { IS_DEMO } from '@/demo/demoMode';
import { ApiClientError } from './apiError';

export { ApiClientError } from './apiError';

/**
 * לקוח ה-API מול ה-Worker. כל קריאה מוגנת שולחת ID Token טרי של Firebase;
 * ההרשאה נבדקת בשרת בכל קריאה — הממשק לעולם לא "מחליט" לבד שמשתמש הוא מנהל.
 */

function baseUrl(): string {
  return webConfig.ok ? webConfig.config.apiBaseUrl : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  user?: User | null;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const attempt = async (forceRefresh: boolean): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.user) headers.Authorization = `Bearer ${await opts.user.getIdToken(forceRefresh)}`;
    try {
      return await fetch(`${baseUrl()}${path}`, {
        method: opts.method ?? 'GET',
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
        signal: opts.signal ?? null,
      });
    } catch {
      throw new ApiClientError('network', 'אין חיבור לשרת. יש לבדוק את החיבור לאינטרנט', 0);
    }
  };

  let res = await attempt(false);
  // טוקן שפג תוקפו בדיוק עכשיו — רענון אחד וניסיון חוזר.
  if (res.status === 401 && opts.user) res = await attempt(true);

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      /* תשובה שאינה JSON */
    }
    const code = body?.error?.code ?? (res.status >= 500 ? 'internal' : 'bad_request');
    throw new ApiClientError(code, body?.error?.message ?? API_ERROR_MESSAGES_HE[code], res.status, body?.error?.retryAfter, body?.error?.fields);
  }
  return (await res.json()) as T;
}

export const api = {
  health: (signal?: AbortSignal): Promise<HealthResponse> =>
    IS_DEMO
      ? Promise.resolve({ ok: true, service: 'crew-worker', version: 'מצב תצוגה', time: new Date().toISOString() })
      : apiRequest<HealthResponse>('/api/health', signal ? { signal } : {}),
  me: (user: User) => apiRequest<MeResponse>('/api/me', { user }),
};
